import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Mobile responsive-table helper. On phones, `.table` elements render as stacked
 * cards (see mobile.css §5 and the phone layer in mobile/mobile-ui.css); for that each
 * `<td>` needs to show its column name. This copies each column's `<thead>` label onto
 * the matching `<td>` via a `data-label` attribute — a safe, React-proof mutation (React
 * ignores foreign data-* attributes on re-render). Re-runs on route change and DOM mutations.
 *
 * It also tags cells so the card can lay itself out:
 *   data-m-empty   — the cell only says "-" / "—" / "N/A": hidden on the card;
 *   data-m-wide    — long text or a form control: takes the card's full width;
 *   data-m-actions — nothing but buttons/links: drawn as the card's action row, no label;
 *   data-m-pair    — a single button right after a value: shares that value's row.
 */
const EMPTY = new Set(['', '-', '—', '–', 'n/a', 'N/A', '—/—']);
const CONTROL = 'input:not([type="checkbox"]):not([type="radio"]), select, textarea';
const ACTION = 'button, a.btn, .btn';

const setFlag = (el: HTMLElement, name: string, on: boolean) => {
  if (on) {
    if (el.getAttribute(name) !== '1') el.setAttribute(name, '1');
  } else if (el.hasAttribute(name)) {
    el.removeAttribute(name);
  }
};

/** The cell's text without sub-badges (`data-m-sub`): «خلال 14 يوماً» under a price must not make
 *  the price «wide» and separate it from its «اعتماد» button. */
const cellText = (td: HTMLElement): string => {
  if (!td.querySelector('[data-m-sub]')) return td.textContent || '';
  const clone = td.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('[data-m-sub]').forEach((n) => n.remove());
  return clone.textContent || '';
};

function tagCell(td: HTMLElement) {
  const text = cellText(td).replace(/\s+/g, ' ').trim();
  const hasMedia = !!td.querySelector('img, svg, input, select, textarea, button, a, canvas, progress');
  setFlag(td, 'data-m-empty', !hasMedia && EMPTY.has(text));
  setFlag(td, 'data-m-wide', text.length > 26 || !!td.querySelector(CONTROL));
  const kids = Array.from(td.children) as HTMLElement[];
  const onlyActions =
    kids.length > 0 &&
    kids.every((k) => k.matches(ACTION) || (k.matches('div, span') && k.children.length > 0 &&
      Array.from(k.children).every((c) => (c as HTMLElement).matches(ACTION))));
  setFlag(td, 'data-m-actions', onlyActions);
  // One button right after a value (a price and its «add to confirmation») shares that value's
  // row on the card, so each button sits next to the price it acts on.
  const prev = td.previousElementSibling as HTMLElement | null;
  const buttons = td.querySelectorAll(ACTION).length;
  setFlag(td, 'data-m-pair',
    onlyActions && buttons === 1 && !!prev && prev !== td.parentElement?.firstElementChild &&
    !prev.hasAttribute('data-m-actions') && !prev.hasAttribute('data-m-empty') && !prev.hasAttribute('data-m-wide'));
}

function applyLabels() {
  if (!window.matchMedia('(max-width: 768px)').matches) return;
  document.querySelectorAll<HTMLTableElement>('table.table').forEach((table) => {
    const heads = Array.from(table.querySelectorAll('thead th')).map((th) => (th.textContent || '').trim());
    table.querySelectorAll('tbody tr').forEach((tr) => {
      Array.from(tr.children).forEach((cell, i) => {
        const td = cell as HTMLElement;
        const label = heads[i];
        if (label && td.getAttribute('data-label') !== label) td.setAttribute('data-label', label);
        tagCell(td);
      });
    });
  });
}

const MobileTableLabels: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Desktop never needs any of this — don't watch every DOM change there.
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    let tid: ReturnType<typeof setTimeout>;
    const run = () => { clearTimeout(tid); tid = setTimeout(applyLabels, 80); };
    run();
    const obs = new MutationObserver(run);
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => { obs.disconnect(); clearTimeout(tid); };
  }, [pathname]);
  return null;
};

export default MobileTableLabels;
